import type { ReviewReasonCategory, ReviewReasonSeverity } from './reviewReason.types';

export type FindingTemplate = {
  id: string;
  severity: ReviewReasonSeverity;
  title: string;
  explanation: string;
  category: ReviewReasonCategory;
  steps: readonly string[];
  recommendedAction?: string;
  /** Lower = higher decision impact within the same severity band. */
  priority: number;
};

/** Maps raw gate checkpoint steps to reviewer-facing finding templates. */
export const REVIEW_FINDING_TEMPLATES: readonly FindingTemplate[] = [
  {
    id: 'employer-not-active',
    severity: 'BLOCKER',
    title: 'Employer could not be confirmed as active',
    explanation:
      'The declared employer could not be verified through available employer records.',
    category: 'EMPLOYER',
    steps: ['REGISTRY_STATUS', 'EMPLOYER_LOOKUP', 'ADDRESS_VERIFICATION', 'PHONE_VERIFICATION'],
    recommendedAction: 'Confirm the employer is active in registry and contact records.',
    priority: 1,
  },
  {
    id: 'income-source-mismatch',
    severity: 'BLOCKER',
    title: "Income source doesn't clearly match the employer",
    explanation:
      "Bank activity doesn't show a clear payroll relationship with the declared employer.",
    category: 'INCOME',
    steps: ['DIRECT_PAYOR_MATCH', 'PAYROLL_PROVIDER_MATCH', 'PERSONAL_TRANSFER_DETECT'],
    recommendedAction: 'Review bank paystubs or deposits against the declared employer.',
    priority: 2,
  },
  {
    id: 'payroll-name-mismatch',
    severity: 'CONCERN',
    title: "Payroll information doesn't match the employer",
    explanation:
      'The payroll name on bank activity does not clearly align with the declared employer.',
    category: 'INCOME',
    steps: ['GOVERNMENT_SCHEME_PAYOR', 'AI_LINKAGE_ANALYSIS'],
    priority: 5,
  },
  {
    id: 'income-pattern-irregular',
    severity: 'CONCERN',
    title: "Income pattern doesn't look like regular employer payroll",
    explanation:
      'Deposit patterns from bank activity do not clearly resemble regular payroll from this employer.',
    category: 'INCOME',
    steps: ['PERSONAL_TRANSFER_DETECT'],
    priority: 8,
  },
  {
    id: 'limited-digital-presence',
    severity: 'CONCERN',
    title: 'Employer has limited online presence',
    explanation: 'Public web and Maps signals need a closer look before you confirm this employer.',
    category: 'DIGITAL_PRESENCE',
    steps: [
      'DIGITAL_FOOTPRINT',
      'WEB_DOMAIN',
      'AI_DIGITAL_FOOTPRINT',
      'OPERATIONAL_STATUS',
      'PLACE_PHOTOS_VISION',
      'SATELLITE_BUILDING_VISION',
    ],
    recommendedAction: 'Review business legitimacy signals before confirming employment.',
    priority: 6,
  },
  {
    id: 'director-legitimacy',
    severity: 'CONCERN',
    title: 'Business legitimacy signals need review',
    explanation:
      'Director or operational legitimacy checks did not fully support this employer profile.',
    category: 'EMPLOYER',
    steps: ['ACTIVE_DIRECTORS'],
    priority: 7,
  },
  {
    id: 'commute-distance-review',
    severity: 'CONCERN',
    title: 'Commute distance exceeds policy limit',
    explanation:
      'The commute between the customer and employer location exceeds the acceptable distance threshold.',
    category: 'LOCATION',
    steps: ['COMMUTE_DISTANCE', 'CROSS_PROVINCE_COMMUTE'],
    recommendedAction:
      'Verify work arrangement and whether the customer actually commutes to this employer location.',
    priority: 3,
  },
  {
    id: 'role-mismatch',
    severity: 'CONCERN',
    title: "Job role doesn't clearly fit the employer",
    explanation:
      'The declared role or sector does not clearly align with this employer type or industry.',
    category: 'ROLE',
    steps: [
      'SECTOR_ROLE_MATCH',
      'DECLARED_SECTOR_VS_EMPLOYER_NAICS',
      'INFRA_MATCH',
      'EMPLOYMENT_CONTEXT_CONTRADICTION',
      'ONSITE_ROLE_VS_REMOTE',
    ],
    recommendedAction: 'Confirm the role and industry fit with the declared employer.',
    priority: 4,
  },
  {
    id: 'location-mismatch',
    severity: 'CONCERN',
    title: "Work location doesn't match the employer address",
    explanation:
      'The declared work location does not align with the employer address on file.',
    category: 'LOCATION',
    steps: ['EMPLOYER_ADDRESS'],
    recommendedAction: 'Verify work location and employer address with the customer.',
    priority: 9,
  },
  {
    id: 'tenure-needs-verification',
    severity: 'CONCERN',
    title: 'Employment dates need verification',
    explanation:
      'Start date, incorporation timing, or tenure signals could not be fully confirmed.',
    category: 'TENURE',
    steps: [
      'FUTURE_START_DATE',
      'MUTATION_CHECK',
      'WORK_AUTHORIZATION',
      'INCORPORATION_DATE',
      'EMPLOYMENT_ELIGIBILITY',
    ],
    recommendedAction: 'Confirm employment start date and authorization with supporting documents.',
    priority: 10,
  },
];

const STEP_TO_TEMPLATE = new Map<string, FindingTemplate>();
for (const template of REVIEW_FINDING_TEMPLATES) {
  for (const step of template.steps) {
    if (!STEP_TO_TEMPLATE.has(step)) {
      STEP_TO_TEMPLATE.set(step, template);
    }
  }
}

export function findingTemplateForStep(step: string): FindingTemplate | null {
  const key = step.trim().toUpperCase();
  return STEP_TO_TEMPLATE.get(key) ?? null;
}

export function normalizeEmployerName(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function employersLikelyConflict(
  declared: string | null | undefined,
  external: string | null | undefined,
): boolean {
  const a = normalizeEmployerName(declared);
  const b = normalizeEmployerName(external);
  if (!a || !b) return false;
  if (a === b) return false;
  if (a.includes(b) || b.includes(a)) return false;
  return true;
}
